import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createBlock as _createBlock, renderList as _renderList, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "tab-game" }
const _hoisted_2 = { class: "tab-header" }
const _hoisted_3 = { class: "tab-content" }
const _hoisted_4 = {
  key: 0,
  class: "empty-data"
}
const _hoisted_5 = { class: "game-add" }
const _hoisted_6 = { class: "game-search" }
const _hoisted_7 = { class: "icon" }
const _hoisted_8 = { class: "name" }
const _hoisted_9 = ["for"]
const _hoisted_10 = ["id", "onChange", "checked"]

import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import store from "@/store";
import trim from "lodash-es/trim";

import { toast } from "@/common/utils/toast";
import { getDate, getHour } from "@/common/utils/dates";

import {
  gameRelatedList,
  gameRelatedListId,
  gameRelatedCreate,
  gameRelatedDelete,
} from "@/common/api/rest/game/related";

import { gameList } from "@/common/api/rest/game/game";

import type { GameRelatedType } from "@/common/types/game/related";
import type { GameType } from "@/common/types/game/game";

import {
  baseAvatar,
  baseConfirm,
  baseLoading,
  baseInput,
  baseModal,
  baseTable,
  svgIcon,
} from "@/common/components";


export default /*@__PURE__*/_defineComponent({
  __name: 'related',
  props: {
    gameId: {}
  },
  setup(__props: any) {

const { t } = useI18n();

const props = __props;

const related = ref<GameRelatedType[]>([]);
const relatedIds = ref<{ item_id: string; game_id: string }[]>([]);
const selected = ref<GameRelatedType | null>(null);

const limit = ref<number>(5);
const page = ref<number>(0);
const pages = ref<number>(0);
const search = ref<string>("");
const searched = ref<number>(0);
const total = ref<number>(0);

const loading = ref(true);
const showModalAddRelated = ref(false);
const showModalRemoveRelated = ref(false);

const gameSearchListRef = ref<any>(null);
const gameSearch = ref<string>("");
const gameSearchList = ref<GameType[]>([]);
const gameLimit = ref<number>(10);
const gamePage = ref<number>(0);
const gamePages = ref<number>(0);
const gameSearched = ref<number>(0);
const gameTotal = ref<number>(0);
const loadingMoreGames = ref(false);

const tableHeader = computed(() => [
  { key: "avatar", label: "" },
  { key: "name", label: t("common.labels.name") },
  { key: "created", label: t("common.labels.created") },
]);

async function fetchRelated() {
  await gameRelatedList(props.gameId, {
    page: page.value,
    limit: limit.value,
    search: search.value,
    sort: "DESC",
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        related.value = data.list;
        pages.value = data.infos.pages;
        total.value = data.infos.total;
        searched.value = data.infos.count;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

async function fetchRelatedId() {
  await gameRelatedListId(props.gameId)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        relatedIds.value = data.list;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

async function searchGames(append = false) {
  if (
    append &&
    (loadingMoreGames.value || gamePage.value + 1 >= gamePages.value)
  ) {
    return;
  }

  if (append) {
    loadingMoreGames.value = true;
  }

  const pageToFetch = append ? gamePage.value + 1 : 0;

  await gameList({
    page: pageToFetch,
    limit: gameLimit.value,
    search: gameSearch.value,
    sort: "ASC",
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        const newItems = (data.list ?? []).filter(
          (item: GameType) => item.game_id !== props.gameId,
        );
        gameSearchList.value = append
          ? [...gameSearchList.value, ...newItems]
          : newItems;
        gamePage.value = pageToFetch;
        gamePages.value = data.infos.pages;
        gameTotal.value = data.infos.total;
        gameSearched.value = data.infos.count;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      loadingMoreGames.value = false;
    });
}

function changePage(value: number) {
  page.value = value;
  fetchRelated();
}

function changeLimit(value: number) {
  limit.value = value;
  page.value = 0;
  fetchRelated();
}

function searchItem(value: string) {
  search.value = value;

  fetchRelated();
}

function resetSearchGame() {
  gameSearch.value = "";
  gameSearchList.value = [];
  gamePage.value = 0;
  gamePages.value = 0;
  gameSearched.value = 0;
  gameTotal.value = 0;
  loadingMoreGames.value = false;
}

async function addRelated() {
  store.commit("SET_LOADING", true);

  resetSearchGame();
  await searchGames();
  store.commit("SET_LOADING", false);

  showModalAddRelated.value = true;
}

async function searchGame() {
  gameSearchList.value = [];
  gamePage.value = 0;
  gamePages.value = 0;
  gameSearched.value = 0;
  gameTotal.value = 0;

  await searchGames();
}

function onGameListScroll(event: Event) {
  const target =
    (event.target as HTMLElement | null) ?? gameSearchListRef.value;

  if (!target) {
    return;
  }

  const reachedEnd =
    target.scrollTop + target.clientHeight >= target.scrollHeight - 24;

  if (
    reachedEnd &&
    !loadingMoreGames.value &&
    gamePage.value + 1 < gamePages.value
  ) {
    void searchGames(true);
  }
}

async function selectGame(item: GameType) {
  if (!relatedIds.value.some((r) => r.game_id === item.game_id)) {
    await gameRelatedCreate(props.gameId, { game_id: item.game_id })
      .then((response) => {
        const { success, message } = response;

        if (success) {
          fetchRelatedId();
          fetchRelated();
        } else {
          toast({ message: message, type: "error" });
        }
      })
      .catch(() => {
        toast({ message: t("error.default"), type: "error" });
      });
  } else {
    const relation = relatedIds.value.find((r) => r.game_id === item.game_id);

    if (!relation) {
      return;
    }

    await gameRelatedDelete(relation.item_id)
      .then((response) => {
        const { success, message } = response;

        if (success) {
          fetchRelatedId();
          fetchRelated();
        } else {
          toast({ message: message, type: "error" });
        }
      })
      .catch(() => {
        toast({ message: t("error.default"), type: "error" });
      });
  }
}

function removeRelated(item: GameRelatedType) {
  selected.value = item;
  showModalRemoveRelated.value = true;
}

function removeRelatedCancel() {
  selected.value = null;
  showModalRemoveRelated.value = false;
}

async function removeRelatedConfirm() {
  if (!selected.value) {
    return;
  }

  await gameRelatedDelete(selected.value.item_id)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchRelatedId();
        fetchRelated();
        showModalRemoveRelated.value = false;
        selected.value = null;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

watch(
  () => gameSearch.value,
  () => {
    if (trim(gameSearch.value).length === 0) {
      searchGame();
    }
  },
);

onMounted(async () => {
  await fetchRelated();
  await fetchRelatedId();
  loading.value = false;
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("section", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _createVNode(_unref(svgIcon), {
          icon: "game",
          size: "2rem"
        }),
        _createTextVNode(" " + _toDisplayString(_unref(t)("collection.labels.related")), 1)
      ]),
      _createElementVNode("div", _hoisted_3, [
        (loading.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
              _createVNode(_unref(baseLoading))
            ]))
          : (_openBlock(), _createBlock(_unref(baseTable), {
              key: 1,
              header: tableHeader.value,
              data: related.value,
              limit: limit.value,
              page: page.value,
              pages: pages.value,
              total: total.value,
              searched: searched.value,
              options: true,
              copy: false,
              edit: false,
              del: true,
              "show-limit": true,
              "onChange:page": _cache[0] || (_cache[0] = ($event: any) => (changePage($event))),
              "onChange:limit": _cache[1] || (_cache[1] = ($event: any) => (changeLimit($event))),
              onSearching: _cache[2] || (_cache[2] = ($event: any) => (searchItem($event))),
              "onBtn:add": addRelated,
              "onBtn:del": _cache[3] || (_cache[3] = ($event: any) => (removeRelated($event)))
            }, {
              avatar: _withCtx(({ data }) => [
                _createVNode(_unref(baseAvatar), {
                  position: "center",
                  "is-edit": false,
                  "show-image": true,
                  url: data?.avatar?.large,
                  "aspect-ratio": 1.333,
                  size: "3rem",
                  "border-radius": "0.25rem"
                }, null, 8, ["url"])
              ]),
              created: _withCtx(({ data }) => [
                _createElementVNode("span", null, _toDisplayString(_unref(getDate)(data.created)) + " - " + _toDisplayString(_unref(getHour)(data.created)), 1)
              ]),
              _: 1
            }, 8, ["header", "data", "limit", "page", "pages", "total", "searched"]))
      ])
    ]),
    _createVNode(_unref(baseConfirm), {
      show: showModalRemoveRelated.value,
      title: _unref(t)('collection.modals.titles.del_related').toUpperCase(),
      message: 
      _unref(t)('collection.modals.messages.del_related_game', {
        name: selected.value?.name?.toUpperCase(),
      })
    ,
      onCancel: _cache[4] || (_cache[4] = ($event: any) => (removeRelatedCancel())),
      onConfirm: _cache[5] || (_cache[5] = ($event: any) => (removeRelatedConfirm()))
    }, null, 8, ["show", "title", "message"]),
    _createVNode(_unref(baseModal), {
      show: showModalAddRelated.value,
      title: _unref(t)('collection.modals.titles.add_related_game').toUpperCase(),
      onClose: _cache[9] || (_cache[9] = ($event: any) => (showModalAddRelated.value = false))
    }, {
      content: _withCtx(() => [
        _createElementVNode("div", _hoisted_5, [
          _createElementVNode("div", _hoisted_6, [
            _createVNode(_unref(baseInput), {
              modelValue: gameSearch.value,
              "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((gameSearch).value = $event)),
              background: "#fff",
              color: "#000",
              placeholder: _unref(t)('common.labels.search'),
              "onKey:enter": _cache[8] || (_cache[8] = ($event: any) => (searchGame()))
            }, {
              sufix: _withCtx(() => [
                _createElementVNode("div", _hoisted_7, [
                  _createVNode(_unref(svgIcon), {
                    icon: "search",
                    size: "1.75rem",
                    onClick: _cache[6] || (_cache[6] = ($event: any) => (searchGame()))
                  })
                ])
              ]),
              _: 1
            }, 8, ["modelValue", "placeholder"])
          ]),
          _createElementVNode("div", {
            ref_key: "gameSearchListRef",
            ref: gameSearchListRef,
            class: "game-list",
            onScroll: onGameListScroll
          }, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(gameSearchList.value, (item, index) => {
              return (_openBlock(), _createElementBlock("div", {
                key: index,
                class: "game"
              }, [
                _createVNode(_unref(baseAvatar), {
                  position: "center",
                  "is-edit": false,
                  "show-image": true,
                  url: item?.avatar?.large,
                  "aspect-ratio": 1.333,
                  size: "3rem",
                  "border-radius": "0.25rem"
                }, null, 8, ["url"]),
                _createElementVNode("div", _hoisted_8, _toDisplayString(item.name), 1),
                _createElementVNode("label", {
                  class: "custom-checkbox",
                  for: `related-game-${item.game_id}`
                }, [
                  _createElementVNode("input", {
                    id: `related-game-${item.game_id}`,
                    class: "checkbox-input",
                    type: "checkbox",
                    onChange: ($event: any) => (selectGame(item)),
                    checked: relatedIds.value.some((r) => r.game_id === item.game_id)
                  }, null, 40, _hoisted_10),
                  _cache[10] || (_cache[10] = _createElementVNode("span", { class: "checkbox-mark" }, null, -1))
                ], 8, _hoisted_9)
              ]))
            }), 128))
          ], 544)
        ])
      ]),
      _: 1
    }, 8, ["show", "title"])
  ], 64))
}
}

})